import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@lmc-env/environment';
import { Observable } from 'rxjs';

export type NLSValidationStatus = 'Valid' | 'Incomplete';

export interface NLSCustomer {
  /** NLS loan account primary key (loanacct.acctrefno). */
  acctrefno: string;
  loanNumber: string;
  customerName: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  province: string | null;
  postalCode: string | null;
  /** Valid only when the customer info AND the bank account would both pass. */
  validationStatus: NLSValidationStatus;
  /** Customer-info fields KapCharge requires but NLS is missing. */
  missingFields: string[];
  /** False when the loan has no active ACH record at all. */
  hasBankAccount: boolean;
  /** Bank-account fields KapCharge requires but the ACH record is missing. */
  bankMissingFields: string[];
  /** 9-digit routing number (0 + institution + transit); null when there is none to check. */
  dprn: string | null;
  /** Whether dprn is in FifBranchCache. null when there was no routing number to look up. */
  branchInCache: boolean | null;
  branchName: string | null;
  institutionCode: string | null;
  transitNumber: string | null;
  /** Last four digits only — the full number is never sent to the browser. */
  accountNumberMasked: string | null;
}

export interface NLSCustomerValidationResponse {
  results: NLSCustomer[];
  /** Rows matching the current search + status filter — drives the paginator. */
  rowCount: number;
  /** Totals across the searched set, independent of the status filter. */
  validCount: number;
  incompleteCount: number;
}

@Injectable({
  providedIn: 'root',
})
export class NlsCustomerValidationService {
  private readonly uri: string = `${environment.DO_URL}/v2`;

  constructor(private readonly http: HttpClient) {}

  /**
   * Lists NLS customers with the customer-info verdict payment processing applies before
   * submitting to KapCharge.
   *
   * @param page 1-indexed page number
   * @param pageSize rows per page
   * @param search matches loan number, first name or last name
   * @param statusFilter 'all' | 'valid' | 'incomplete'
   */
  list(
    page: number,
    pageSize: number,
    search?: string,
    statusFilter?: string
  ): Observable<NLSCustomerValidationResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    if (search?.trim()) {
      params = params.set('search', search.trim());
    }

    if (statusFilter && statusFilter !== 'all') {
      params = params.set('status', statusFilter);
    }

    return this.http.get<NLSCustomerValidationResponse>(
      `${this.uri}/nls-customer-validation`,
      { params }
    );
  }
}
